import { View, Text, Image, ImageBackground, StyleSheet, TouchableOpacity } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useResponsive } from '../../../hooks/useResponsive';
import { FONTS } from '../../../constants/config';
import { createShadow } from '../../../utils/styles';
import { HeroBannerData } from '../types';

const bannerBg = require('../assets/banner_bg.png');
const bannerOverlay = require('../assets/banner_overlay.png');
const bannerLogo = require('../assets/banner_logo.png');

interface HeroBannerProps {
  data: HeroBannerData;
  isStrategyTeam?: boolean;
  onEdit?: () => void;
}

export default function HeroBanner({ data, isStrategyTeam, onEdit }: HeroBannerProps) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const { isMobile } = useResponsive();

  return (
    <View
      style={[
        styles.outerContainer,
        createShadow(0, 1, 3, '#000', 0.1),
      ]}
    >
      <View style={[styles.innerFrame, { aspectRatio: isMobile ? 3 : 4 }]}>
        <ImageBackground
          source={data.coverImage ? { uri: data.coverImage } : bannerBg}
          style={styles.imageBackground}
          imageStyle={styles.imageStyle}
          resizeMode={data.coverImage ? 'contain' : 'cover'}
        >
          <LinearGradient
            colors={['transparent', 'rgba(0,0,0,0.5)']}
            start={{ x: 0.5, y: 0.3 }}
            end={{ x: 0.5, y: 1 }}
            style={styles.gradient}
          >
            {/* Bottom section with overlay pattern */}
            <View style={styles.bottomSection}>
              <Image
                source={bannerOverlay}
                style={styles.overlayImage}
                resizeMode="cover"
              />
              <View
                style={[
                  styles.content,
                  { flexDirection: isRTL ? 'row-reverse' : 'row' },
                ]}
              >
                {/* Logo + Text on the start side (right in RTL) */}
                <View style={[styles.logoContainer, createShadow(0, 10, 40, '#000', 0.15)]}>
                  <Image
                    source={data.logoImage ? { uri: data.logoImage } : bannerLogo}
                    style={styles.logo}
                    resizeMode="contain"
                  />
                </View>

                <View style={styles.textContainer}>
                  <Text
                    style={[
                      styles.title,
                      { textAlign: isRTL ? 'right' : 'left' },
                      isMobile && styles.titleMobile,
                    ]}
                  >
                    {data.organizationName}
                  </Text>
                  <Text
                    style={[
                      styles.subtitle,
                      { textAlign: isRTL ? 'right' : 'left' },
                      isMobile && styles.subtitleMobile,
                    ]}
                  >
                    {data.subtitle}
                  </Text>
                </View>

                {/* Spacer */}
                <View style={{ flex: 1 }} />

                {/* Edit icon on the end side (left in RTL) */}
                {isStrategyTeam && (
                  <TouchableOpacity
                    style={styles.editButton}
                    onPress={onEdit}
                  >
                    <Ionicons name="create-outline" size={18} color="#FFFFFF" />
                  </TouchableOpacity>
                )}
              </View>
            </View>
          </LinearGradient>
        </ImageBackground>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    overflow: 'hidden',
    width: '100%',
  },
  innerFrame: {
    borderRadius: 4,
    overflow: 'hidden',
    margin: 4,
  },
  imageBackground: {
    flex: 1,
    width: '100%',
    backgroundColor: '#F8F8F8',
  },
  imageStyle: {
    borderRadius: 4,
  },
  gradient: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  bottomSection: {
    position: 'relative',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  overlayImage: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
    opacity: 0.3,
  },
  content: {
    alignItems: 'center',
    gap: 12,
  },
  editButton: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    width: 80,
    height: 80,
    borderRadius: 2,
  },
  textContainer: {
    gap: 4,
    justifyContent: 'center',
  },
  title: {
    fontSize: 20,
    fontFamily: FONTS.bold || FONTS.medium,
    color: '#FFFFFF',
  },
  titleMobile: {
    fontSize: 18,
  },
  subtitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: 'rgba(255,255,255,0.85)',
  },
  subtitleMobile: {
    fontSize: 12,
  },
});
